import * as React from "react"

import { cn } from "~/utils/helpers"

export type ProgressBarSize = "sm" | "md" | "lg" | "xl"

/** Stands in for the figure when `max` gives nothing to measure against. */
const NO_TOTAL = "\u2014"

/** Track height, and the matching floor: the smallest fill is a circle one bar high. */
const SIZES: Record<ProgressBarSize, { track: string; floor: string }> = {
  sm: { track: "h-1.5", floor: "min-w-1.5" },
  md: { track: "h-2", floor: "min-w-2" },
  lg: { track: "h-4", floor: "min-w-4" },
  xl: { track: "h-6", floor: "min-w-6" },
}

type Measured = {
  max: number
  /** Clamped to the track, so the fill and the announced value cannot disagree. */
  value: number
  /** Share of the track the fill covers, 0 to 100. */
  fill: number
  /** Share the overflow segment covers; 0 unless the value is past `max` and overflow is on. */
  over: number
  /** False when `max` gives nothing to measure against, so there is no share to draw or announce. */
  hasTotal: boolean
}

/**
 * Past `max` both segments are scaled against the value rather than the total, so the pair
 * still spans one track: at 132% that is 100/132 filled and 32/132 over.
 *
 * A total of zero (a budget row with no target) has no share to express, so the bar stays empty
 * rather than dividing by it or standing a made-up 100 in its place. Overflow makes no difference:
 * there is nothing to be past.
 */
const measure = (value: number, max: number, allowOverflow: boolean): Measured => {
  if (!Number.isFinite(max) || max <= 0) {
    return { max: 0, value: 0, fill: 0, over: 0, hasTotal: false }
  }
  const raw = Number.isFinite(value) ? Math.max(value, 0) : 0
  if (!allowOverflow || raw <= max) {
    const clamped = Math.min(raw, max)
    return { max, value: clamped, fill: (clamped / max) * 100, over: 0, hasTotal: true }
  }
  return {
    max,
    value: max,
    fill: (max / raw) * 100,
    over: ((raw - max) / raw) * 100,
    hasTotal: true,
  }
}

export type ProgressBarProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "role" | "aria-valuenow" | "aria-valuemin" | "aria-valuemax"
> & {
  /** How far the quantity has advanced. Clamped to `0…max` unless `allowOverflow` is set. */
  value: number
  /**
   * The known total `value` is measured against. Defaults to 100, so `value` reads as a percentage.
   * A total of zero or less leaves the track empty and the figure an em dash: there is no share to
   * show. Budget rows with no target rely on this.
   */
  max?: number
  /** Accessible name — what is being measured, e.g. "Data quality score". Never the value itself. */
  label: string
  /** Track height: 6, 8 (default), 16 or 24px. */
  size?: ProgressBarSize
  /**
   * Show what went past `max` as a second segment instead of clamping. Only for quantities that
   * can legitimately exceed their total, such as spend against budget.
   */
  allowOverflow?: boolean
  /**
   * Render a header row above the bar: `label` on the left, `valueText` on the right. Off by
   * default, since most call sites already lay out their own name and figure.
   */
  showLabel?: boolean
  /**
   * Caller-formatted figure for the header's right side ("72%", "7 of 12 collections"). Past
   * `max` it stays the plain total in the same format ("132%") — the second segment is what says
   * it went over. Also becomes the announced value, so the visible figure and what assistive tech
   * reads never disagree. Ignored when there is no total: both read an em dash instead.
   */
  valueText?: string
  /** Animate width changes. 350ms, already suppressed under `prefers-reduced-motion`. */
  animate?: boolean
  /** Class applied to the filled portion. */
  fillClassName?: string
}

/**
 * Determinate progress bar. Full width by default; zero renders no fill, and above zero no
 * segment is ever narrower than the bar is tall. The bar itself never carries text: the optional
 * `showLabel` header sits above it.
 * Canonical intent: {@link ../../design-system/intents.ts PROGRESS_BAR_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value,
      max = 100,
      label,
      size = "md",
      allowOverflow = false,
      showLabel = false,
      valueText,
      animate = true,
      className,
      fillClassName,
      ...props
    },
    ref,
  ) => {
    const measured = measure(value, max, allowOverflow)
    const { track, floor } = SIZES[size]
    const overflowing = measured.over > 0
    const motion =
      animate && "transition-[width] duration-[350ms] ease-in-out motion-reduce:transition-none"
    const segment = cn("rounded-full h-full", floor, motion)
    // valuenow has to stay inside the track, so past max the real figure is spelled out instead.
    const fallbackValueText = overflowing ? `${value} of ${measured.max}` : undefined
    // With no total there is no percentage to state, and none to announce either.
    const shownValue = measured.hasTotal ? valueText : NO_TOTAL

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-label={label}
        aria-valuenow={measured.value}
        aria-valuemin={0}
        aria-valuemax={measured.max}
        aria-valuetext={measured.hasTotal ? (valueText ?? fallbackValueText) : NO_TOTAL}
        className={cn("flex w-full flex-col gap-2", className)}
        {...props}
      >
        {showLabel && (
          // Already announced as the bar's name and value; hidden so it isn't read twice.
          <div aria-hidden className="flex items-center justify-between gap-2">
            <span className="label-sm text-fg-base truncate min-w-0">{label}</span>
            {shownValue && (
              <span className="paragraph-sm text-fg-subtle tabular-nums shrink-0 text-right">
                {shownValue}
              </span>
            )}
          </div>
        )}
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-full",
            track,
            // The pair fills the track when overflowing, so the base would only show through the gap.
            overflowing ? "flex gap-1" : "bg-progress-bar-base shadow-progress-bar-border",
          )}
        >
          {measured.fill > 0 && (
            <div
              style={{ width: `${measured.fill}%` }}
              className={cn(
                segment,
                "bg-progress-bar-highlight highlight-gradient shadow-progress-bar-highlight-border",
                fillClassName,
              )}
            />
          )}
          {overflowing && (
            <div
              style={{ width: `${measured.over}%` }}
              className={cn(
                segment,
                "bg-progress-bar-overflow highlight-gradient shadow-progress-bar-highlight-border",
              )}
            />
          )}
        </div>
      </div>
    )
  },
)
ProgressBar.displayName = "ProgressBar"

export { ProgressBar }
